import { LoginInfo, ApiInterface } from '../types';
import { handleSuccess, handleError, getCookie, sleep, AC_GET_USERINFO_API, getLoginInfo, report } from '../helper';
import JsApi from '@tencent/tvf-jsapi';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Base } from './base';

const clearAcCookies = () => {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  cookies.forEach((cookie) => {
    const [key] = cookie.split('=');
    Cookies.remove(key, { domain: 'ac.qq.com' });
  });
};

/**
 * 腾讯体育
 */
export class AcApi extends Base implements ApiInterface {
  public needRefresh = false; // 是否需要续期，外部可以根据此属性进行事件监听并在续期后处理逻辑
  public refreshed = false;
  public ifRefreshSuccess = false;

  /**
   * 是否在客户端内
   */
  private isInApp: boolean;

  public constructor({ isInApp }: { isInApp: boolean }) {
    super();
    this.isInApp = isInApp;
  }

  /**
   * 登录
   */
  @report('login')
  public async login() {
    // 端内
    if (this.isInApp) {
      const { errCode, errMsg } = await JsApi.invoke('actionLogin');
      if (errCode !== 0) {
        return handleError(errCode, errMsg);
      }
      return handleSuccess(true);
    }

    // 端外登录
    const { protocol, href } = window.location;
    const redirectUrl = encodeURIComponent(`${protocol}//m.ac.qq.com/loginSuccess.html?back_url=${encodeURIComponent(href)}`);
    // qq互联登录方式
    window.location.href = `https://graph.qq.com/oauth2.0/authorize?response_type=code&client_id=101483258&redirect_uri=${redirectUrl}`;

    // 如果业务侧代码有 location.reload()，那么等待两秒返回以保证 href 正确跳转
    await sleep(2000);

    return handleSuccess(true);
  }

  /**
   * 退出登录
   */
  @report('logout')
  public async logout(needReload = true) {
    // 端内
    if (this.isInApp) {
      return handleError(-1, '腾讯动漫不支持 jsBridge 退出登录，需要在 app 中退出');
    }

    // 未登录不允许调用
    const { result: isLogin } = await this.isLogin();
    if (!isLogin) {
      return handleError(-1, '当前未登录，无法退出登录');
    }

    // 端外清空 cookie
    clearAcCookies();
    if (needReload) {
      location.reload();
    }
    return handleSuccess(true);
  }

  /**
   * 是否登录
   */
  @report('isLogin')
  public async isLogin() {
    // 端内
    if (this.isInApp) {
      try {
        const { errCode, errMsg } = await JsApi.invoke('isLogin');
        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }
        return handleSuccess(true);
      } catch (error) {
        return handleSuccess(false);
      }
    }

    // 端外
    const openid = getCookie('open_id');
    return handleSuccess(!!openid);
  }

  /**
   * 获取登录信息，cookie
   */
  @report('getLoginInfo')
  public async getLoginInfo() {
    return getLoginInfo({
      isInApp: this.isInApp,
      loginInfoHandler: (_loginInfo: LoginInfo, appCookies: Record<string, string>) => {
        const loginInfo = _loginInfo;

        if (this.isInApp) {
          loginInfo.appid = getCookie('appid', appCookies);
          loginInfo.openid = getCookie('openid', appCookies);
          loginInfo.accessToken = getCookie('access_token', appCookies);
          loginInfo.refreshToken = getCookie('refresh_token', appCookies);
        } else {
          loginInfo.accessToken = getCookie('access_token');
          loginInfo.openid = getCookie('open_id');
        }
      },
    });
  }

  /**
   * 获取用户信息，头像昵称
   */
  @report('getUserInfo')
  public async getUserInfo() {
    const userInfo = {
      nickname: '',
      avatar: '',
    };

    // 端内
    if (this.isInApp) {
      try {
        const { errCode, errMsg, result } = await JsApi.invoke('getMainUserInfo');
        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }
        userInfo.nickname = result?.userInfo?.nickname || '';
        userInfo.avatar = result?.userInfo?.headImgUrl || '';
      } catch (error) {
        return handleError(-1, '未登录，无法获取用户信息');
      }
    } else {
      const { result: isLogin } = await this.isLogin();
      if (!isLogin) {
        return handleError(-1, '未登录，无法获取用户信息');
      }

      const res = await axios.get(AC_GET_USERINFO_API, {
        withCredentials: true,
        params: {
          login_type: getCookie('login_type'),
        },
      });
      const { data } = res.data;
      userInfo.avatar = data.head;
      userInfo.nickname = data.nickname;
    }

    return handleSuccess(userInfo);
  }
}
